import { RequirePayoutsAccountingSettingsUser } from '@/components/auth/RequirePayoutsAccountingSettingsUser';
import {
  useGetOrganizationClawbackSettingsQuery,
  usePutOrganizationClawbackSettingsMutation,
} from '@/services/organizations';
import { OrganizationNode } from '@/types/Organizations';
import { Box, Table, useOpenClose } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useParams } from 'react-router-dom';

import {
  MilestoneClawbackSettingDisplayMap,
  MilestoneClawbackSettingKey,
  MaterialClawbackSettingDisplayMap,
  MaterialClawbackSettingKey,
  OrganizationSettingsV2Types,
  ProgramType,
  OrganizationMilestoneClawbackSettingsV2,
  OrganizationMaterialClawbackSettingsV2,
} from 'types';

import { OrganizationSettingsCard } from '../shared/OrganizationSettingsCard';

import { ClawbackSettingsEditModal } from './ClawbackSettingsEditModal';

interface ClawbacksSettingsProps {
  organization: OrganizationNode;
  isLoading?: boolean;
  programType: ProgramType;
}
export const ClawbacksSetting = ({ organization, programType }: ClawbacksSettingsProps) => {
  const { alias } = useParams<{ alias: any; programType: any }>();

  const [setClawbackSettings] = usePutOrganizationClawbackSettingsMutation();

  const {
    data: milestoneClawbackSettings,
    isFetching: isLoadingMilestoneClawbackSettings,
    error: milestoneClawbackSettingsError,
  } = useGetOrganizationClawbackSettingsQuery({
    alias,
    programType,
    clawbackType: OrganizationSettingsV2Types.milestoneClawbackSettings,
  });

  const {
    data: materialClawbackSettings,
    isFetching: isLoadingMaterialClawbackSettings,
    error: materialClawbackSettingsError,
  } = useGetOrganizationClawbackSettingsQuery({
    alias,
    programType,
    clawbackType: OrganizationSettingsV2Types.materialClawbackSettings,
  });

  const milestoneClawbackSettingsData = {
    ntpClawbackDays: milestoneClawbackSettings?.value?.ntpClawbackDays,
    installClawbackDays: milestoneClawbackSettings?.value?.installClawbackDays,
  } as OrganizationMilestoneClawbackSettingsV2;

  const materialClawbackSettingsData = {
    partnerInstallSubmittal: materialClawbackSettings?.value?.partnerInstallSubmittal,
    partnerInstallApproval: materialClawbackSettings?.value?.partnerInstallApproval,
  } as OrganizationMaterialClawbackSettingsV2;

  const {
    handleClose: handleCloseMilestoneClawbackSettingModal,
    handleOpen: handleOpenMilestoneClawbackSettingModal,
    isOpen: isOpenMilestoneClawbackSettingsModal,
  } = useOpenClose();

  const {
    handleClose: handleCloseMaterialClawbackSettingModal,
    handleOpen: handleOpenMaterialClawbackSettingModal,
    isOpen: isOpenMaterialClawbackSettingsModal,
  } = useOpenClose();

  const handleSubmitMilestoneClawbackSettings = async (values: any) => {
    const payload = {
      alias,
      clawbackSettings: {
        value: {
          ntpClawbackDays: values.ntpClawbackDays,
          installClawbackDays: values.installClawbackDays,
        },
        programType,
        inherit: values.inherit,
      },
      clawbackType:
        OrganizationSettingsV2Types.milestoneClawbackSettings as OrganizationSettingsV2Types.milestoneClawbackSettings,
    };
    await setClawbackSettings(payload).unwrap();
    handleCloseMilestoneClawbackSettingModal();
  };

  const handleSubmitMaterialClawbackSettings = async (values: any) => {
    const payload = {
      alias,
      clawbackSettings: {
        value: {
          partnerInstallSubmittal: values.partnerInstallSubmittal,
          partnerInstallApproval: values.partnerInstallApproval,
        },
        programType,
        inherit: values.inherit,
      },
      clawbackType:
        OrganizationSettingsV2Types.materialClawbackSettings as OrganizationSettingsV2Types.materialClawbackSettings,
    };
    await setClawbackSettings(payload).unwrap();
    handleCloseMaterialClawbackSettingModal();
  };

  const milestoneClawbackSettingsRows = milestoneClawbackSettingsData
    ? Object.entries(milestoneClawbackSettingsData).map(([key, value]) => {
        return {
          name: MilestoneClawbackSettingDisplayMap[key as MilestoneClawbackSettingKey] || key,
          days: value,
        };
      })
    : [];

  const materialClawbackSettingsRows = materialClawbackSettingsData
    ? Object.entries(materialClawbackSettingsData).map(([key, value]) => {
        return {
          name: MaterialClawbackSettingDisplayMap[key as MaterialClawbackSettingKey] || key,
          days: value,
        };
      })
    : [];

  const columnConfig = [
    {
      heading: 'Rule Name',
      dataKey: 'name',
      width: 100,
    },
    {
      heading: 'Days',
      dataKey: 'days',
      width: 100,
    },
  ];

  return (
    <>
      <ClawbackSettingsEditModal
        isOpen={isOpenMilestoneClawbackSettingsModal}
        handleClose={handleCloseMilestoneClawbackSettingModal}
        handleSubmit={handleSubmitMilestoneClawbackSettings}
        currentClawbackSettings={milestoneClawbackSettingsData}
        modalId="milestone-clawback-settings-modal"
        modalTitle="Edit Milestone Clawback Settings"
        submitButtonText="Save Changes"
        isInherited={milestoneClawbackSettings?.inherit ?? false}
        displayMap={MilestoneClawbackSettingDisplayMap}
        defaultValues={{
          ntpClawbackDays: 14,
          installClawbackDays: 14,
        }}
      />

      <Box>
        <OrganizationSettingsCard
          cardTitle="Milestone Clawback Settings"
          organization={organization}
          isLoading={isLoadingMilestoneClawbackSettings}
          error={milestoneClawbackSettingsError}
          isInherited={!milestoneClawbackSettingsError && milestoneClawbackSettings?.inherit}
          onClick={handleOpenMilestoneClawbackSettingModal}
          renderEditButton={(button) => (
            <RequirePayoutsAccountingSettingsUser>{button}</RequirePayoutsAccountingSettingsUser>
          )}
        >
          <Table
            rowKey="id"
            columns={columnConfig}
            rows={milestoneClawbackSettingsRows}
            isLoading={isLoadingMilestoneClawbackSettings}
          />
          <Box padding="xl xl xl md">
            {milestoneClawbackSettings?.inherit && (
              <>
                Settings effective as of{' '}
                {milestoneClawbackSettings.effectiveDate &&
                  DateTime.fromISO(milestoneClawbackSettings.effectiveDate.toString())
                    .toLocal()
                    .toFormat('MM/dd/yyyy')}
              </>
            )}
          </Box>
        </OrganizationSettingsCard>
      </Box>

      <ClawbackSettingsEditModal
        isOpen={isOpenMaterialClawbackSettingsModal}
        handleClose={handleCloseMaterialClawbackSettingModal}
        handleSubmit={handleSubmitMaterialClawbackSettings}
        currentClawbackSettings={materialClawbackSettingsData}
        modalId="material-clawback-settings-modal"
        modalTitle="Edit Material Clawback Settings"
        submitButtonText="Save Changes"
        isInherited={materialClawbackSettings?.inherit ?? false}
        displayMap={MaterialClawbackSettingDisplayMap}
        defaultValues={{
          partnerInstallSubmittal: 14,
          partnerInstallApproval: 14,
        }}
      />
      <Box margin="md 0 0 0">
        <OrganizationSettingsCard
          cardTitle="Material Clawback Settings"
          organization={organization}
          isLoading={isLoadingMaterialClawbackSettings}
          error={materialClawbackSettingsError}
          isInherited={!materialClawbackSettingsError && materialClawbackSettings?.inherit}
          onClick={handleOpenMaterialClawbackSettingModal}
          renderEditButton={(button) => (
            <RequirePayoutsAccountingSettingsUser>{button}</RequirePayoutsAccountingSettingsUser>
          )}
        >
          <Table
            rowKey="id"
            columns={columnConfig}
            rows={materialClawbackSettingsRows}
            isLoading={isLoadingMaterialClawbackSettings}
          />
          <Box padding="xl xl xl md">
            {materialClawbackSettings?.inherit && (
              <>
                Settings effective as of{' '}
                {materialClawbackSettings.effectiveDate &&
                  DateTime.fromISO(materialClawbackSettings.effectiveDate.toString())
                    .toLocal()
                    .toFormat('MM/dd/yyyy')}
              </>
            )}
          </Box>
        </OrganizationSettingsCard>
      </Box>
    </>
  );
};
